import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * Alpha Vantage API服务
 * 需要API密钥
 * 支持股票、指数、外汇和加密货币
 */
class AlphaVantageService {
  private client = axios.create({
    baseURL: 'https://www.alphavantage.co/query',
    timeout: 10000,
  });

  // API密钥（优先级：用户配置 > 环境变量）
  private apiKey: string | null = null;

  constructor() {
    // 优先级 1: 优先从 settingsStore（用户配置）读取
    try {
      const { useSettingsStore } = require('@/stores/settingsStore');
      const apiKeys = useSettingsStore.getState().apiKeys;
      if (apiKeys?.alphavantage && apiKeys.alphavantage.trim() !== '') {
        this.apiKey = apiKeys.alphavantage;
        return; // 如果找到用户配置，直接返回，不再检查环境变量
      }
    } catch (e) {
      // 忽略错误，继续检查环境变量
    }

    // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
    this.apiKey = (import.meta as any)?.env?.VITE_ALPHAVANTAGE_API_KEY || null;
  }

  /**
   * 设置API密钥
   */
  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  /**
   * 获取股票价格
   */
  async getStockPrice(symbol: string): Promise<AssetPrice> {
    try {
      if (!this.apiKey) {
        throw new Error('Alpha Vantage API key is required');
      }

      const response = await this.client.get('', {
        params: {
          function: 'GLOBAL_QUOTE',
          symbol: symbol,
          apikey: this.apiKey,
        },
      });

      const quote = response.data['Global Quote'];
      if (!quote || !quote['05. price']) {
        throw new Error(`Stock not found: ${symbol}`);
      }

      const currentPrice = parseFloat(quote['05. price']);
      const priceChange = parseFloat(quote['09. change']) || 0;
      const priceChangePercent =
        parseFloat(quote['10. change percent']?.replace('%', '')) || 0;
      const lastUpdated = quote['07. latest trading day']
        ? new Date(quote['07. latest trading day']).toISOString()
        : new Date().toISOString();

      return {
        id: symbol.toLowerCase(),
        symbol: symbol.toUpperCase(),
        name: quote['01. symbol'] || symbol,
        current_price: currentPrice,
        price_change_24h: priceChange,
        price_change_percentage_24h: priceChangePercent,
        last_updated: lastUpdated,
        source_url: 'https://www.alphavantage.co',
      };
    } catch (error) {
      console.error('Alpha Vantage获取股票价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取外汇汇率
   */
  async getForexRate(from: string, to: string): Promise<AssetPrice> {
    try {
      if (!this.apiKey) {
        throw new Error('Alpha Vantage API key is required');
      }

      const response = await this.client.get('', {
        params: {
          function: 'CURRENCY_EXCHANGE_RATE',
          from_currency: from.toUpperCase(),
          to_currency: to.toUpperCase(),
          apikey: this.apiKey,
        },
      });

      const exchangeRate = response.data['Realtime Currency Exchange Rate'];
      if (!exchangeRate) {
        throw new Error(`Forex rate not found: ${from}/${to}`);
      }

      const currentPrice = parseFloat(exchangeRate['5. Exchange Rate']);
      const lastUpdated = exchangeRate['6. Last Refreshed']
        ? new Date(exchangeRate['6. Last Refreshed']).toISOString()
        : new Date().toISOString();

      // Alpha Vantage外汇API不提供24小时变化，设为0
      return {
        id: `${from.toLowerCase()}_${to.toLowerCase()}`,
        symbol: `${from.toUpperCase()}/${to.toUpperCase()}`,
        name: `${from.toUpperCase()}/${to.toUpperCase()}`,
        current_price: currentPrice,
        price_change_24h: 0,
        price_change_percentage_24h: 0,
        last_updated: lastUpdated,
        source_url: 'https://www.alphavantage.co',
      };
    } catch (error) {
      console.error('Alpha Vantage获取外汇汇率失败:', error);
      throw error;
    }
  }

  /**
   * 获取加密货币价格
   */
  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    try {
      if (!this.apiKey) {
        throw new Error('Alpha Vantage API key is required');
      }

      const results: AssetPrice[] = [];

      for (const id of ids) {
        try {
          const symbol = this.convertIdToSymbol(id);
          const response = await this.client.get('', {
            params: {
              function: 'CRYPTO_INTRADAY',
              symbol: symbol,
              market: 'USD',
              interval: '1min',
              apikey: this.apiKey,
            },
          });

          const metadata = response.data['Meta Data'];
          const timeSeries = response.data[`Time Series (1min)`];

          if (!metadata || !timeSeries) {
            continue;
          }

          const timeKeys = Object.keys(timeSeries);
          if (timeKeys.length === 0) {
            continue;
          }

          const latestTime = timeKeys[0];
          if (!latestTime) {
            continue;
          }

          const latestData = timeSeries[latestTime];
          if (
            !latestData ||
            typeof latestData !== 'object' ||
            !('4. close' in latestData)
          ) {
            continue;
          }
          const currentPrice = parseFloat(latestData['4. close']);

          // 计算24小时变化（需要获取24小时前的数据）
          const priceChange24h = 0; // Alpha Vantage需要额外调用获取
          const priceChangePercent24h = 0;

          results.push({
            id: id.toLowerCase(),
            symbol: symbol,
            name: this.getAssetName(id),
            current_price: currentPrice,
            price_change_24h: priceChange24h,
            price_change_percentage_24h: priceChangePercent24h,
            last_updated: new Date(latestTime).toISOString(),
            source_url: 'https://www.alphavantage.co',
          });
        } catch (error) {
          console.warn(`Failed to get price for ${id}:`, error);
          continue;
        }
      }

      return results;
    } catch (error) {
      console.error('Alpha Vantage获取加密货币价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取历史数据
   */
  async getHistoricalData(
    id: string,
    symbol: string,
    type: 'stock' | 'crypto' | 'forex',
    days: number = 7
  ): Promise<HistoricalData> {
    try {
      if (!this.apiKey) {
        throw new Error('Alpha Vantage API key is required');
      }

      let functionName = '';
      let interval = 'daily';

      if (type === 'stock') {
        functionName = 'TIME_SERIES_DAILY';
      } else if (type === 'crypto') {
        functionName = 'DIGITAL_CURRENCY_DAILY';
        interval = 'daily';
      } else {
        throw new Error('Historical data not supported for forex');
      }

      const response = await this.client.get('', {
        params: {
          function: functionName,
          symbol: symbol,
          market: type === 'crypto' ? 'USD' : undefined,
          apikey: this.apiKey,
        },
      });

      const timeSeriesKey =
        type === 'crypto'
          ? `Time Series (Digital Currency Daily)`
          : `Time Series (Daily)`;
      const timeSeries = response.data[timeSeriesKey];

      if (!timeSeries) {
        throw new Error('No historical data available');
      }

      const prices: [number, number][] = Object.entries(timeSeries)
        .slice(0, days)
        .map(([date, data]: [string, any]): [number, number] => [
          new Date(date).getTime(),
          parseFloat(data[type === 'crypto' ? '4a. close (USD)' : '4. close']),
        ])
        .reverse();

      const market_caps: [number, number][] = prices.map(
        ([time, price]): [number, number] => [time, price * 1000000]
      );
      const total_volumes: [number, number][] = Object.entries(timeSeries)
        .slice(0, days)
        .map(([date, data]: [string, any]): [number, number] => [
          new Date(date).getTime(),
          parseFloat(data[type === 'crypto' ? '5. volume' : '5. volume']) || 0,
        ])
        .reverse();

      return {
        prices,
        market_caps,
        total_volumes,
      };
    } catch (error) {
      console.error('Alpha Vantage获取历史数据失败:', error);
      throw error;
    }
  }

  /**
   * 转换ID为符号
   */
  private convertIdToSymbol(id: string): string {
    const symbolMap: Record<string, string> = {
      bitcoin: 'BTC',
      btc: 'BTC',
      ethereum: 'ETH',
      eth: 'ETH',
    };

    return symbolMap[id.toLowerCase()] || id.toUpperCase();
  }

  /**
   * 获取资产名称
   */
  private getAssetName(id: string): string {
    const nameMap: Record<string, string> = {
      bitcoin: 'Bitcoin',
      btc: 'Bitcoin',
      ethereum: 'Ethereum',
      eth: 'Ethereum',
    };

    return (
      nameMap[id.toLowerCase()] ||
      id.charAt(0).toUpperCase() + id.slice(1).toLowerCase()
    );
  }
}

export const alphavantageService = new AlphaVantageService();
